import React, { createContext, useState, useEffect, useContext } from 'react';
import { buildApiUrl } from '../services/apiConfig';

const UseCaseContext = createContext();

export const useUseCases = () => {
  const context = useContext(UseCaseContext);
  if (!context) {
    throw new Error('useUseCases must be used within UseCaseProvider');
  }
  return context;
};

// Helper to create URL-friendly slug from use case name
export const createSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

export const UseCaseProvider = ({ children }) => {
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUseCases = async () => {
      try {
        setLoading(true);
        const response = await fetch(buildApiUrl('AUTH', '/fetch/use_cases'));
        
        if (!response.ok) {
          throw new Error(`Failed to fetch use cases: ${response.status}`);
        }
        
        const data = await response.json();
        const useCaseData = data.data || [];
        
        // Enrich with slug for URL routing
        const enrichedUseCases = useCaseData.map(uc => ({
          ...uc,
          slug: createSlug(uc.name)
        }));
        
        setUseCases(enrichedUseCases);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching use cases:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchUseCases();
  }, []);

  // Helper functions
  const getUseCaseById = (id) => {
    return useCases.find(uc => uc.id === id);
  };

  const getUseCaseBySlug = (slug) => {
    return useCases.find(uc => uc.slug === slug);
  };

  const getUseCaseByName = (name) => {
    return useCases.find(uc => uc.name === name);
  };

  // Generate dynamic route for use case
  const getUseCaseRoute = (useCaseIdOrName) => {
    const useCase = getUseCaseById(useCaseIdOrName) || getUseCaseByName(useCaseIdOrName);
    if (!useCase) return null;
    return `/use-case/${useCase.slug}`;
  };

  const value = {
    useCases,
    loading,
    error,
    getUseCaseById,
    getUseCaseBySlug,
    getUseCaseByName,
    getUseCaseRoute,
    refresh: () => window.location.reload()
  };

  return (
    <UseCaseContext.Provider value={value}>
      {children}
    </UseCaseContext.Provider>
  );
};

export default UseCaseContext;
